import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Field } from 'react-final-form';
import moment from 'moment';
import { Box, Button, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAsyncFn } from 'react-use';
import { toast } from 'react-toastify';
import CustomTextField from 'components/Forms/TextField';
import CustomSelect from 'components/Forms/CustomSelect';
import CustomDatePicker from 'components/Forms/CustomDatePicker';
import CustomTimePicker from 'components/Forms/CustomTimePicker';
import { ROLES } from 'appConstants';
import {
  getTrainingModulesList,
  getCompanyTrainingModuleSettings,
  getCompanyTrainingModuleTrainers,
} from 'http/trainingModules';
import { getTrainingPartsList, getTrainingPart } from 'http/trainingParts';
import { generateErrorObject, resolveApiErrorMessage } from 'utils';
import getTrainingSessionMaxEndDate from 'utils/getTrainingSessionMaxEndDate';
import { canLaunchEntity } from 'utils/entityLifecycleStatus';
import {
  formatTrainingModuleLabel,
  formatTrainingPartLabel,
} from 'pages/Students/Edit/TrainingSessionDetails/helpers/formatTrainingLabels';
import validateTrainingSessionForm from './validation';
import configErrors from './configErrors';
import resolveTrainingSessionMoodleLanguageFieldError from './helpers/resolveTrainingSessionMoodleLanguageFieldError';

const TRAINING_SESSION_STATUSES = ['draft', 'scheduled', 'active', 'completed', 'cancelled'];
const LAUNCHED_PART_STATUSES = new Set(['scheduled', 'active', 'retired']);
const CANCELABLE_SESSION_STATUSES = new Set(['scheduled', 'active']);

const canCancelTrainingSession = status => CANCELABLE_SESSION_STATUSES.has(status);
const DELIVERY_METHODS_BY_TRAINING_METHOD = {
  theory: ['classroom', 'virtual_classroom', 'elearning'],
  ojt: ['on_the_job'],
  practical: ['classroom', 'on_the_job'],
};

const useStyles = makeStyles(theme => ({
  actions: {
    marginTop: 24,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  lifecycleActions: {
    display: 'flex',
    alignItems: 'center',
  },
  formActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginLeft: 'auto',
  },
}));

const toSelectOption = (value, label, extra = {}) => ({ value, label, ...extra });

const GLOBAL_STATION_VALUE = '';

const defaultPartEnabledForMethod = trainingMethod => trainingMethod === 'theory';

const isPartEnabledForCompany = (part, partSettingsById, settingsAvailable) => {
  if (!settingsAvailable) {
    return true;
  }
  if (partSettingsById.has(part.id)) {
    return partSettingsById.get(part.id);
  }
  return defaultPartEnabledForMethod(part.trainingMethod);
};

const syncEndDateAfterStartChange = (form, newStartDate, selectedPart) => {
  if (!newStartDate) {
    form.change('endDate', null);
    return;
  }

  const currentEndDate = form.getState().values.endDate;
  if (!currentEndDate) {
    return;
  }

  if (moment(currentEndDate).isBefore(moment(newStartDate), 'day')) {
    form.change('endDate', null);
    return;
  }

  const maxEndDate = getTrainingSessionMaxEndDate({
    startDate: newStartDate,
    availableTo: selectedPart?.availableTo,
    validityMonthCount: selectedPart?.validityMonthCount,
  });

  if (maxEndDate && moment(currentEndDate).isAfter(moment(maxEndDate), 'day')) {
    form.change('endDate', null);
  }
};

const isPartSelectableForSession = part => {
  if (!LAUNCHED_PART_STATUSES.has(part.status)) {
    return false;
  }

  if (part.status === 'retired') {
    return false;
  }

  const now = Date.now();
  const availableTo = part.availableTo ? new Date(part.availableTo).getTime() : null;

  if (availableTo != null && availableTo < now) {
    return false;
  }

  return true;
};

const TrainingSessionForm = ({
  mode,
  initialValues,
  onSubmitForm,
  onLaunch,
  onCancelEntity,
  backUrl,
  submitting,
  readOnly = false,
}) => {
  const classes = useStyles();
  const history = useHistory();
  const { t } = useTranslation('common');
  const { station: profileStation, role } = useSelector(state => state.auth.profile);
  const { stations } = useSelector(state => state.stations);
  const currentStatus = initialValues?.status || 'draft';
  const showLaunch = mode === 'edit' && !readOnly && canLaunchEntity(currentStatus) && Boolean(onLaunch);
  const showCancelEntity =
    mode === 'edit' && !readOnly && canCancelTrainingSession(currentStatus) && Boolean(onCancelEntity);

  const stationOptions = useMemo(() => {
    const options = (stations || []).map(station => toSelectOption(station.id, station.name));
    if (role === ROLES.SUPER) {
      return [toSelectOption(GLOBAL_STATION_VALUE, t('trainingSessions.scopes.global')), ...options];
    }
    return options;
  }, [role, stations, t]);

  const [moduleOptions, setModuleOptions] = useState([]);
  const [partOptions, setPartOptions] = useState([]);
  const [partMetaById, setPartMetaById] = useState({});
  const [trainerOptions, setTrainerOptions] = useState([]);
  const [selectedModuleId, setSelectedModuleId] = useState(initialValues?.trainingModuleId || null);
  const [selectedPartMethod, setSelectedPartMethod] = useState(initialValues?.trainingMethod || null);

  const statusOptions = useMemo(
    () =>
      TRAINING_SESSION_STATUSES.map(value => ({
        value,
        label: t(`trainingSessions.statuses.${value}`),
      })),
    [t],
  );

  const buildDeliveryMethodOptions = part => {
    const methods = part?.trainingMethod
      ? DELIVERY_METHODS_BY_TRAINING_METHOD[part.trainingMethod] || []
      : selectedPartMethod
      ? DELIVERY_METHODS_BY_TRAINING_METHOD[selectedPartMethod] || []
      : [];

    return methods.map(value => {
      const isElearningWithoutLms =
        value === 'elearning' && part != null && part.trainingMethod === 'theory' && !part.hasMoodleLink;
      const baseLabel = t(`trainingSessions.deliveryMethods.${value}`);
      return {
        value,
        label: isElearningWithoutLms ? `${baseLabel} (${t('trainingSessions.elearningNoLms')})` : baseLabel,
        isDisabled: isElearningWithoutLms,
      };
    });
  };

  const [, loadModules] = useAsyncFn(async () => {
    const response = await getTrainingModulesList({ pageSize: 500 });
    if (!response.error && !response.statusCode) {
      setModuleOptions(
        (response.items || []).map(module => toSelectOption(module.id, `${module.code} — ${module.name}`)),
      );
    }
    return response;
  }, []);

  const [, loadParts] = useAsyncFn(
    async (moduleId, preservePartId) => {
      if (!moduleId) {
        setPartOptions([]);
        setPartMetaById({});
        return null;
      }
      const [response, settings] = await Promise.all([
        getTrainingPartsList({
          trainingModuleId: moduleId,
          pageSize: 500,
        }),
        getCompanyTrainingModuleSettings(moduleId),
      ]);
      if (!response.error && !response.statusCode) {
        const partSettingsById = new Map();
        const settingsAvailable =
          settings &&
          !settings.error &&
          !(settings.statusCode && settings.statusCode >= 400) &&
          Array.isArray(settings.partSettings);
        if (settingsAvailable) {
          settings.partSettings.forEach(item => {
            partSettingsById.set(item.trainingPartId, item.isEnabled);
          });
        }

        const metaMap = {};
        const options = (response.items || [])
          .filter(part => isPartSelectableForSession(part) || part.id === preservePartId)
          .map(part => {
            metaMap[part.id] = part;
            const isPartDisabled = !isPartEnabledForCompany(part, partSettingsById, settingsAvailable);
            const methodLabel = t(`trainingModules.tabs.${part.trainingMethod}`, {
              defaultValue:
                part.trainingMethod === 'ojt' ? 'OJT' : part.trainingMethod,
            });
            const baseLabel = `${part.code} ${part.version}.${part.revision} (${methodLabel})`;
            const label = isPartDisabled ? `${baseLabel} (${t('trainingSessions.partDisabled')})` : baseLabel;
            return toSelectOption(part.id, label, { isPartDisabled });
          });
        setPartMetaById(metaMap);
        setPartOptions(options);
      }
      return response;
    },
    [t],
  );

  const [, loadTrainers] = useAsyncFn(async moduleId => {
    if (!moduleId) {
      setTrainerOptions([]);
      return null;
    }
    const response = await getCompanyTrainingModuleTrainers(moduleId);
    if (!response.error && !(response.statusCode && response.statusCode >= 400)) {
      setTrainerOptions(
        (response.items || []).map(item =>
          toSelectOption(item.userId, `${item.firstName} ${item.lastName}`.trim() || item.email),
        ),
      );
    }
    return response;
  }, []);

  useEffect(() => {
    if (initialValues?.trainingModuleId) {
      setSelectedModuleId(initialValues.trainingModuleId);
    }
    if (initialValues?.trainingMethod) {
      setSelectedPartMethod(initialValues.trainingMethod);
    }
  }, [initialValues?.trainingMethod, initialValues?.trainingModuleId]);

  useEffect(() => {
    if (readOnly) {
      return;
    }
    loadModules();
  }, [loadModules, readOnly]);

  useEffect(() => {
    if (readOnly) {
      return;
    }
    if (selectedModuleId) {
      loadParts(selectedModuleId, mode === 'edit' ? initialValues?.trainingPartId : undefined);
      loadTrainers(selectedModuleId);
    }
  }, [initialValues?.trainingPartId, loadParts, loadTrainers, mode, readOnly, selectedModuleId]);

  useEffect(() => {
    if (readOnly || mode !== 'edit' || !initialValues?.trainingPartId) {
      return undefined;
    }

    let isCancelled = false;

    const seedSessionPartMeta = async () => {
      const response = await getTrainingPart(initialValues.trainingPartId);
      if (isCancelled || response.error || response.statusCode) {
        return;
      }

      setPartMetaById(prev => ({
        ...prev,
        [response.id]: response,
      }));
    };

    seedSessionPartMeta();

    return () => {
      isCancelled = true;
    };
  }, [initialValues?.trainingPartId, mode, readOnly]);

  const formInitialValues = useMemo(() => {
    const defaultStationId =
      initialValues?.stationId ?? (mode === 'create' && role !== ROLES.SUPER ? profileStation?.id ?? null : null);

    return {
      station:
        defaultStationId != null
          ? stationOptions.find(option => option.value === defaultStationId) ||
            toSelectOption(defaultStationId, initialValues?.stationName || String(defaultStationId))
          : mode === 'edit'
          ? stationOptions.find(option => option.value === GLOBAL_STATION_VALUE) ||
            toSelectOption(GLOBAL_STATION_VALUE, t('trainingSessions.scopes.global'))
          : null,
      trainingModule:
        initialValues?.trainingModuleId != null
          ? toSelectOption(
              initialValues.trainingModuleId,
              formatTrainingModuleLabel(
                initialValues.trainingModuleCode,
                initialValues.trainingModuleName,
              ) ||
                moduleOptions.find(option => option.value === initialValues.trainingModuleId)?.label ||
                initialValues.trainingModuleCode ||
                String(initialValues.trainingModuleId),
            )
          : null,
      trainingPart:
        initialValues?.trainingPartId != null
          ? toSelectOption(
              initialValues.trainingPartId,
              formatTrainingPartLabel(
                initialValues.trainingPartVersionCode,
                initialValues.trainingMethod,
              ) ||
                partOptions.find(option => option.value === initialValues.trainingPartId)?.label ||
                initialValues.trainingPartVersionCode ||
                String(initialValues.trainingPartId),
            )
          : null,
      deliveryMethod: initialValues?.deliveryMethod
        ? toSelectOption(
            initialValues.deliveryMethod,
            t(`trainingSessions.deliveryMethods.${initialValues.deliveryMethod}`),
          )
        : null,
      trainer:
        initialValues?.trainerUserId != null
          ? trainerOptions.find(option => option.value === initialValues.trainerUserId) ||
            toSelectOption(initialValues.trainerUserId, initialValues.trainerName)
          : null,
      status: initialValues?.status
        ? statusOptions.find(option => option.value === initialValues.status) || statusOptions[0]
        : statusOptions[0],
      location: initialValues?.location || '',
      description: initialValues?.description || '',
      maxStudents: initialValues?.maxStudents != null ? String(initialValues.maxStudents) : '',
      startDate: initialValues?.startDate ? moment(initialValues.startDate).toDate() : null,
      endDate: initialValues?.endDate ? moment(initialValues.endDate).toDate() : null,
      startTime: initialValues?.startTime ? moment(initialValues.startTime, ['HH:mm:ss', 'HH:mm']).toDate() : null,
      endTime: initialValues?.endTime ? moment(initialValues.endTime, ['HH:mm:ss', 'HH:mm']).toDate() : null,
    };
  }, [
    initialValues,
    mode,
    moduleOptions,
    partOptions,
    profileStation?.id,
    role,
    stationOptions,
    statusOptions,
    t,
    trainerOptions,
  ]);

  const handleSubmit = async values => {
    const selectedPartId = values.trainingPart?.value ?? initialValues?.trainingPartId;
    const selectedPart =
      partMetaById[selectedPartId] ||
      (selectedPartMethod || initialValues?.trainingMethod
        ? {
            trainingMethod: selectedPartMethod || initialValues?.trainingMethod,
            availableFrom: initialValues?.availableFrom,
            availableTo: initialValues?.availableTo,
            validityMonthCount: initialValues?.validityMonthCount,
          }
        : null);
    const currentPartMethod =
      selectedPart?.trainingMethod || selectedPartMethod || initialValues?.trainingMethod || null;
    const isElearningDelivery = values.deliveryMethod?.value === 'elearning' && currentPartMethod === 'theory';

    const validationErrors = validateTrainingSessionForm(values, { selectedPart });
    if (Object.keys(validationErrors).length > 0) {
      return validationErrors;
    }

    const payload = {
      stationId:
        values.station?.value === GLOBAL_STATION_VALUE || values.station?.value == null ? null : values.station.value,
      trainingModuleId: values.trainingModule?.value,
      trainingPartId: values.trainingPart?.value,
      deliveryMethod: values.deliveryMethod?.value,
      trainerUserId: values.trainer?.value ?? null,
      location: values.location?.trim() || null,
      description: values.description?.trim() || '',
      maxStudents:
        isElearningDelivery || values.maxStudents === '' || values.maxStudents == null
          ? null
          : Number(values.maxStudents),
      startDate: values.startDate ? moment(values.startDate).format('DD.MM.YYYY') : null,
      endDate: values.endDate ? moment(values.endDate).format('DD.MM.YYYY') : null,
      startTime: isElearningDelivery || !values.startTime ? null : moment(values.startTime).format('HH:mm'),
      endTime: isElearningDelivery || !values.endTime ? null : moment(values.endTime).format('HH:mm'),
    };

    if (mode === 'create') {
      payload.status = 'draft';
    }

    if (mode === 'edit') {
      delete payload.trainingModuleId;
      delete payload.trainingPartId;
    }

    const result = await onSubmitForm(payload);
    if (result?.error || (result?.statusCode && result.statusCode >= 400)) {
      const moodleLanguageError = resolveTrainingSessionMoodleLanguageFieldError(result.message, t);
      if (moodleLanguageError) {
        return { deliveryMethod: moodleLanguageError };
      }

      const mappedErrors = generateErrorObject(result.message, configErrors);
      if (Object.keys(mappedErrors).length > 0) {
        return mappedErrors;
      }

      toast.error(
        resolveApiErrorMessage(result.message, {
          t,
          fallbackMessage: t('errors.serverError'),
        }),
      );
      return {};
    }
    return undefined;
  };

  const handleCancel = () => {
    if (backUrl) {
      history.push(backUrl);
    }
  };

  return (
    <Form
      key={initialValues?.id || 'create'}
      initialValues={formInitialValues}
      onSubmit={handleSubmit}
      render={({ handleSubmit: submitForm, submitting: formSubmitting, form, values, dirty }) => {
        const selectedDelivery = values?.deliveryMethod?.value;
        const selectedPartId = values?.trainingPart?.value ?? initialValues?.trainingPartId;
        const selectedPart = partMetaById[selectedPartId] || null;
        const currentPartMethod =
          selectedPart?.trainingMethod || selectedPartMethod || initialValues?.trainingMethod || null;
        const isElearningDelivery = selectedDelivery === 'elearning' && currentPartMethod === 'theory';
        const deliveryMethodOptions = buildDeliveryMethodOptions(selectedPart);
        const partAvailableFrom = selectedPart?.availableFrom
          ? moment(selectedPart.availableFrom).startOf('day').toDate()
          : undefined;
        const partAvailableTo = selectedPart?.availableTo
          ? moment(selectedPart.availableTo).endOf('day').toDate()
          : undefined;
        const sessionMaxEndDate = values.startDate
          ? getTrainingSessionMaxEndDate({
              startDate: values.startDate,
              availableTo: selectedPart?.availableTo,
              validityMonthCount: selectedPart?.validityMonthCount,
            })
          : null;
        const endDateMax = sessionMaxEndDate ? moment(sessionMaxEndDate).endOf('day').toDate() : partAvailableTo;
        const endDateMin = values.startDate ? moment(values.startDate).startOf('day').toDate() : partAvailableFrom;
        const isScheduleOnline = isElearningDelivery;
        const isStartDateLocked = currentStatus !== 'draft';
        const isEndDateDisabled = readOnly || submitting || formSubmitting || !values.startDate;
        const isSaveDisabled = readOnly || submitting || formSubmitting || (mode === 'edit' && !dirty);

        return (
          <form
            onSubmit={event => {
              if (readOnly) {
                event.preventDefault();
                return;
              }
              submitForm(event);
            }}
          >
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Field name="trainingModule" type="text">
                  {({ input, meta }) => (
                    <CustomSelect
                      input={input}
                      meta={meta}
                      id="trainingModule"
                      label={t('trainingSessions.trainingModule')}
                      options={moduleOptions}
                      isDisabled={mode === 'edit' || readOnly || submitting || formSubmitting}
                      fullWidth
                      onChange={
                        mode === 'edit'
                          ? undefined
                          : option => {
                              input.onChange(option);
                              setSelectedModuleId(option?.value || null);
                              setSelectedPartMethod(null);
                              form.change('trainingPart', null);
                              form.change('deliveryMethod', null);
                              form.change('trainer', null);
                            }
                      }
                    />
                  )}
                </Field>
              </Grid>
              <Grid item xs={12} md={6}>
                <Field name="trainingPart" type="text">
                  {({ input, meta }) => (
                    <CustomSelect
                      input={input}
                      meta={meta}
                      id="trainingPart"
                      label={t('trainingSessions.trainingPart')}
                      options={partOptions}
                      isDisabled={mode === 'edit' || readOnly || submitting || formSubmitting || !selectedModuleId}
                      fullWidth
                      onChange={
                        mode === 'edit'
                          ? undefined
                          : option => {
                              input.onChange(option);
                              const part = partMetaById[option?.value];
                              setSelectedPartMethod(part?.trainingMethod || null);
                              form.change('deliveryMethod', null);
                            }
                      }
                    />
                  )}
                </Field>
              </Grid>
              <Grid item xs={12} md={6}>
                <Field name="station" type="text">
                  {({ input, meta }) => (
                    <CustomSelect
                      input={input}
                      meta={meta}
                      id="station"
                      label={t('tablesLabels.station')}
                      options={stationOptions}
                      isDisabled={readOnly || submitting || formSubmitting || !stationOptions.length}
                      isClearable={role === ROLES.SUPER}
                      fullWidth
                    />
                  )}
                </Field>
              </Grid>
              {values?.trainingPart?.value ? (
                <Grid item xs={12}>
                  <Box component="p" style={{ margin: 0, color: 'rgba(0, 0, 0, 0.6)', fontSize: 14 }}>
                    {t('trainingSessions.sessionScopeHint', {
                      scope:
                        values?.station?.value == null || values?.station?.value === GLOBAL_STATION_VALUE
                          ? t('trainingSessions.scopes.global')
                          : t('trainingSessions.scopes.local'),
                    })}
                  </Box>
                </Grid>
              ) : null}
              <Grid item xs={12} md={6}>
                <Field name="deliveryMethod" type="text">
                  {({ input, meta }) => (
                    <CustomSelect
                      input={input}
                      meta={meta}
                      id="deliveryMethod"
                      label={t('trainingSessions.deliveryMethod')}
                      options={deliveryMethodOptions}
                      isDisabled={readOnly || submitting || formSubmitting || !deliveryMethodOptions.length}
                      isOptionDisabled={option => Boolean(option?.isDisabled || option?.isPartDisabled)}
                      fullWidth
                      onChange={option => {
                        if (option?.isDisabled) {
                          return;
                        }
                        input.onChange(option);
                      }}
                    />
                  )}
                </Field>
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  type="text"
                  id="trainer"
                  name="trainer"
                  component={CustomSelect}
                  label={t('formLabels.trainers')}
                  placeholder={t('placeholders.chooseTrainer')}
                  options={trainerOptions}
                  isDisabled={readOnly || submitting || formSubmitting}
                  isClearable
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  type="text"
                  id="status"
                  name="status"
                  component={CustomSelect}
                  label={t('tablesLabels.status')}
                  options={statusOptions}
                  isDisabled
                  fullWidth
                />
              </Grid>
              {!isScheduleOnline ? (
                <>
                  <Grid item xs={12} md={6}>
                    <Field
                      type="text"
                      id="maxStudents"
                      name="maxStudents"
                      component={CustomTextField}
                      label={t('formLabels.maxStudents')}
                      variant="outlined"
                      disabled={readOnly || submitting || formSubmitting}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Field
                      type="text"
                      id="location"
                      name="location"
                      component={CustomTextField}
                      label={t('formLabels.location')}
                      variant="outlined"
                      disabled={readOnly || submitting || formSubmitting}
                      fullWidth
                    />
                  </Grid>
                </>
              ) : null}
              <Grid item xs={12}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Field name="startDate" type="text">
                      {({ input, meta }) => (
                        <CustomDatePicker
                          input={{
                            ...input,
                            onChange: date => {
                              input.onChange(date);
                              syncEndDateAfterStartChange(form, date, selectedPart);
                            },
                          }}
                          meta={meta}
                          id="startDate"
                          label={t(isScheduleOnline ? 'formLabels.availableFrom' : 'formLabels.startDate')}
                          disabled={
                            readOnly || submitting || formSubmitting || isStartDateLocked
                          }
                          minDate={partAvailableFrom}
                          maxDate={partAvailableTo}
                          fullWidth
                        />
                      )}
                    </Field>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Field
                      type="text"
                      id="endDate"
                      name="endDate"
                      component={CustomDatePicker}
                      label={t(isScheduleOnline ? 'formLabels.availableUntil' : 'formLabels.endDate')}
                      disabled={isEndDateDisabled}
                      minDate={endDateMin}
                      maxDate={endDateMax}
                      fullWidth
                    />
                  </Grid>
                  {!isScheduleOnline ? (
                    <>
                      <Grid item xs={12} md={6}>
                        <Field
                          type="text"
                          id="startTime"
                          name="startTime"
                          component={CustomTimePicker}
                          label={t('formLabels.startTime')}
                          disabled={readOnly || submitting || formSubmitting}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Field
                          type="text"
                          id="endTime"
                          name="endTime"
                          component={CustomTimePicker}
                          label={t('formLabels.endTime')}
                          disabled={readOnly || submitting || formSubmitting}
                          fullWidth
                        />
                      </Grid>
                    </>
                  ) : null}
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Field
                  type="text"
                  id="description"
                  name="description"
                  component={CustomTextField}
                  label={t('tablesLabels.description')}
                  variant="outlined"
                  multiline
                  rows={4}
                  disabled={readOnly || submitting || formSubmitting}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Box className={classes.actions}>
              {!readOnly && (showLaunch || showCancelEntity) ? (
                <Box className={classes.lifecycleActions}>
                  {showCancelEntity ? (
                    <Button variant="outlined" disabled={submitting || formSubmitting} onClick={() => onCancelEntity()}>
                      {t('trainingSessions.cancelEntity', 'Mark as cancelled')}
                    </Button>
                  ) : null}
                  {showLaunch ? (
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={submitting || formSubmitting}
                      onClick={() => onLaunch()}
                    >
                      {t('trainingSessions.launch', 'Launch session')}
                    </Button>
                  ) : null}
                </Box>
              ) : null}
              <Box className={classes.formActions}>
                <Button variant="outlined" onClick={handleCancel} disabled={submitting || formSubmitting}>
                  {t('buttons.cancel')}
                </Button>
                <Button type="submit" variant="contained" color="primary" disabled={isSaveDisabled}>
                  {t('buttons.save')}
                </Button>
              </Box>
            </Box>
          </form>
        );
      }}
    />
  );
};

TrainingSessionForm.propTypes = {
  mode: PropTypes.oneOf(['create', 'edit']).isRequired,
  initialValues: PropTypes.object,
  onSubmitForm: PropTypes.func.isRequired,
  onLaunch: PropTypes.func,
  onCancelEntity: PropTypes.func,
  backUrl: PropTypes.string,
  submitting: PropTypes.bool,
  readOnly: PropTypes.bool,
};

TrainingSessionForm.defaultProps = {
  initialValues: null,
  onLaunch: null,
  onCancelEntity: null,
  backUrl: null,
  submitting: false,
  readOnly: false,
};

export default TrainingSessionForm;
